/* eslint-disable react-hooks/exhaustive-deps */
import { AppContext } from "@/App";
import OverviewComponent, { ProjectOverviewRow } from "../Components/OverviewComponent";
import ProjectHealth from "../Components/ProjectHealth";
import { RadialChartComponent } from "../Components/RadialChartComponent";
import WorkstreamHealthReport from "../Components/WorkstreamHealth";
import WorkstreamProgressChart, { WorkstreamProgress } from "../Components/WorkstreamProgressChart";
import { useContext, useEffect } from "react";
import NoProjects from "./NoProjects";
import Loading from "@/Components/loading";
import { WorkstreamHealth } from "@/Components/StatusReport/StatusReportChart";
import React from "react";
import { RaidNotification } from "@/Components/Notifications";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/Components/ui/dialog";
import FrmMultiProjectDashboard from "@/Components/MultiProjectDashboard/frmMultiProjectDashboard";
import FrmDashViewOptions from "@/Components/MultiProjectDashboard/frmDashViewOptions";
import { RefreshIcon } from "@/Components/ui/Icons/TooltipIcon";
import { useIsMobile } from "@/hooks/use-mobile";
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";
import { useTheme } from "@/Components/theme-provider";


export type WorkstreamOverview = {
    actual: number;
    planned: number;
    workstreamHealth: WorkstreamHealth; // name is part of the workstream health
}

export type DashboardData = {
    workstreams: WorkstreamOverview[];
    projectHealth: WorkstreamHealth;
    projectOverview: ProjectOverviewRow[]
    currentWeek: number;
    totalWeeks: number;
    notifications: RaidNotification[];
}

const MultiProjectDash = () => {
    const app = useContext(AppContext);
    const isMobile = useIsMobile();
    const zoomLevel = useResponsiveZoom();
    const { theme } = useTheme();

    const [dialogTitle, setDialogTitle] = React.useState("Group Edit Tasks");
    const [dialogContent, setDialogContent] = React.useState<JSX.Element | null>(null);
    const [dialogOpen, setDialogOpen] = React.useState(false);

    const [isLoading, setIsLoading] = React.useState(true);

    const [selectedProjects, setSelectedProjects] = React.useState<number[]>(app.applicationUser?.userSettings?.dashboardProjects || app.projects.map(p => p.id));
    const [selectedViewElements, setSelectedViewElements] = React.useState<number>(app.applicationUser?.userSettings?.dashboardElements || 31); // Default to all view elements selected (31 = 1+2+4+8+16)

    const [workstreamHealth, setWorkstreamHealth] = React.useState<WorkstreamHealth[]>([]);
    const [projectHealth, setProjectHealth] = React.useState<WorkstreamHealth>();
    const [projectProgressActual, setProjectProgressActual] = React.useState(0);
    const [projectProgressPlanned, setProjectProgressPlanned] = React.useState(0);
    const [workstreamProgress, setWorkstreamProgress] = React.useState<WorkstreamProgress[]>([]);
    const [projectOverview, setProjectOverview] = React.useState<ProjectOverviewRow[]>([]);
    const [currentWeek, setCurrentWeek] = React.useState(0);
    const [totalWeeks, setTotalWeeks] = React.useState(0);
    const [notifications, setNotifications] = React.useState<RaidNotification[]>([]);

    useEffect(() => {
        if (!app.applicationUser?.userSettings?.dashboardProjects) {
            app.loadUserSettings().then((data) => {
                if (data) {
                    loadDashboardData(data.dashboardProjects ? data.dashboardProjects : app.projects.map(p => p.id));
                    setSelectedViewElements(data.dashboardElements ? data.dashboardElements : 31);
                }
            });
        }
        else {
            loadDashboardData(app.applicationUser.userSettings.dashboardProjects);
        }
    }, [app.projects, app.applicationUser]);

    function loadDashboardData(projects: number[] = selectedProjects) {
        if (app.applicationUser && app.applicationUser.projectUserRoles.length == 0) {
            setIsLoading(false);
            return;
        }

        if (selectedProjects.length === 0 && app.projects.length === 0) {
            setIsLoading(true);
            return;
        }

        // Clear previous data when selectedProject changes
        setWorkstreamProgress([]);
        setProjectProgressActual(0);
        setProjectProgressPlanned(0);
        setProjectHealth(undefined);
        setWorkstreamHealth([]);
        setProjectOverview([]);
        setCurrentWeek(0);
        setTotalWeeks(0);

        setIsLoading(true);

        if (projects.length > 0) {
            const queryString = projects.map(id => `ids=${id}`).join('&');
            fetch(`Reports/MultiProjectDashboard?${queryString}`)
                .then((response) => response.json())
                .then(data => {
                    processDashboardData(data);
                    setSelectedProjects(projects);
                    setIsLoading(false);
                })
                .catch((error) => console.error("Error fetching data:", error));
        }
    }

    function processDashboardData(data: DashboardData) {
        setWorkstreamProgress(data.workstreams.map(ws => {
            return {
                name: ws.workstreamHealth.workstreamName ?? "",
                workstreamId: ws.workstreamHealth.workstreamId ?? 0,
                actual: ws.actual,
                planned: ws.planned,
                scheduleStatus: ws.workstreamHealth.schedule
            }
        }));

        const projectPlanned = data.workstreams.reduce((acc, ws) => acc + ws.planned, 0);
        const projectActual = data.workstreams.reduce((acc, ws) => acc + ws.actual, 0);


        setProjectProgressActual(Math.round(projectActual / data.workstreams.length));
        setProjectProgressPlanned(Math.round(projectPlanned / data.workstreams.length));

        setProjectHealth(data.projectHealth);

        setWorkstreamHealth(data.workstreams.map(ws => ws.workstreamHealth));
        setProjectOverview(data.projectOverview);

        setCurrentWeek(data.currentWeek);
        setTotalWeeks(data.totalWeeks);
        setNotifications(data.notifications || []);
    }

    function showDashboardOptions() {
        setDialogTitle("Select Projects");
        setDialogContent(
            <FrmMultiProjectDashboard
                setDialogOpen={setDialogOpen}
                selectedProjects={selectedProjects}
                setSelectedProjects={updateSelectedProjects}
            />
        );
        setDialogOpen(true);
    }

    function updateSelectedProjects(newProjects: number[]) {
        setSelectedProjects(newProjects);
        if (app.applicationUser) {
            app.updateUserSettings("dashboardProjects", newProjects.join(','));
        }
        loadDashboardData(newProjects);
    }

    function showViewOptions() {
        setDialogTitle("View Options");
        setDialogContent(
            <FrmDashViewOptions
                setDialogOpen={setDialogOpen}
                selectedViewElements={selectedViewElements}
                setSelectedViewElements={updateSelectedViewElements}
            />
        );
        setDialogOpen(true);
    }

    function updateSelectedViewElements(newElements: number) {
        setSelectedViewElements(newElements);
        if (app.applicationUser) {
            app.updateUserSettings("dashboardElements", newElements.toString());
        }
    }

    function filterProjectsByHealth(status: 1 | 2 | 4, statusName: "overall" | "scope" | "schedule" | "resources" | "financial" | "other") {
        let filteredProjects: number[] = selectedProjects;

        switch (statusName) {
            case "overall":
                filteredProjects = workstreamHealth
                    .filter(item => item.overall === status)
                    .map(item => item.workstreamId);
                break;
            case "scope":
                filteredProjects = workstreamHealth
                    .filter(item => item.scope === status)
                    .map(item => item.workstreamId);
                break;
            case "schedule":
                filteredProjects = workstreamHealth
                    .filter(item => item.schedule === status)
                    .map(item => item.workstreamId);
                break;
            case "resources":
                filteredProjects = workstreamHealth
                    .filter(item => item.resources === status)
                    .map(item => item.workstreamId);
                break;
            case "financial":
                filteredProjects = workstreamHealth
                    .filter(item => item.financial === status)
                    .map(item => item.workstreamId);
                break;
            case "other":
                filteredProjects = workstreamHealth
                    .filter(item => item.other === status)
                    .map(item => item.workstreamId);
                break;
        }

        setSelectedProjects(filteredProjects);
        if (app.applicationUser) {
            app.updateUserSettings("dashboardProjects", filteredProjects.join(','));
        }
        loadDashboardData(filteredProjects);
    }

    function resetFilter() {
        setSelectedProjects(app.projects.map(p => p.id));
        if (app.applicationUser) {
            app.updateUserSettings("dashboardProjects", app.projects.map(p => p.id).join(','));
        }
        loadDashboardData(app.projects.map(p => p.id));
    }

    function radialClick() {
        if ((selectedViewElements & 8) === 8) {
            // add momentary glow to WorkstreamProgressChart
            const chartElement = document.getElementById('workstream-progress-chart');
            if (chartElement) {
                chartElement.classList.add('shadow-[0_0_10px_5px_rgba(255,255,255,0.5)]');
                setTimeout(() => {
                    chartElement.classList.remove('shadow-[0_0_10px_5px_rgba(255,255,255,0.5)]');
                }, 1000);
            }
        } else {
            setSelectedViewElements(selectedViewElements + 8);
        }
    }

    if (isLoading) {
        return <Loading />
    }
    if (app.applicationUser && app.applicationUser.projectUserRoles.length == 0) {
        return <NoProjects />
    }
    
    // Mobile version - simplified layout
    if (isMobile) {
        return (
            <div className="flex flex-col h-full overflow-auto relative" style={{ zoom: zoomLevel }}>
                <div className="sticky top-0 z-10 bg-background border-b px-2 py-2 space-y-2">
                    {/* <div className="flex items-center justify-between">
                        <div className="fixed top-2 right-2 z-50">
                            <button
                                className="p-2 rounded-full bg-blue-600"
                                onClick={() => { loadDashboardData(); }}
                            >
                                <RefreshIcon className="w-7 h-7" />
                            </button>
                        </div>
                    </div> */}
                    <div className="flex gap-1">
                        {selectedProjects.length < app.projects.length && (
                            <button
                                onClick={() => resetFilter()}
                                className="flex-1 button-default whitespace-nowrap"
                            >
                                Show All
                            </button>
                        )}
                        <button
                            onClick={showDashboardOptions}
                            className="flex-1 button-default"
                        >
                            Projects
                        </button>
                        <button
                            onClick={showViewOptions}
                            className="flex-1 button-default"
                        >
                            View
                        </button>
                    </div>
                </div>
                <div className="p-2 space-y-4 mb-8">
                    {(selectedViewElements & 4) === 4 && projectHealth && <ProjectHealth projectHealth={projectHealth} currentWeek={currentWeek} totalWeeks={totalWeeks} notifications={notifications} setNotifications={setNotifications} isMultiProject={true} handleHealthClick={filterProjectsByHealth} />}
                    
                    {(selectedViewElements & 1) === 1 && (
                        <div className="w-full">
                            <RadialChartComponent actualPercentage={projectProgressActual} plannedPercentage={projectProgressPlanned} isMultiProject={true} onClick={radialClick} isMobile={isMobile} />
                        </div>
                    )}
                    {(selectedViewElements & 2) === 2 && projectOverview && <OverviewComponent projectOverviewRows={projectOverview} isMultiProject={true} />}
                    {(selectedViewElements & 8) === 8 && <WorkstreamProgressChart workstreamProgress={workstreamProgress} isMultiProject={true} />}
                    {(selectedViewElements & 16) === 16 && <WorkstreamHealthReport workstreamHealth={workstreamHealth} isMultiProject={true} showProjectName={(selectedViewElements & 8) !== 8} />}
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="flex flex-col w-[95vw] max-w-none max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-xl">{dialogTitle}</DialogTitle>
                        </DialogHeader>
                        {dialogContent}
                    </DialogContent>
                </Dialog>
            </div>
        );
    }
    
    // Desktop/Tablet version - with zoom
    return (
        <div 
            className="flex flex-col h-full overflow-auto"
            style={{
                zoom: zoomLevel
            }}
        >
            <div className={"flex flex-row justify-between items-center px-4 py-2" + (theme === "light" ? " glassmorphism-card" : "")}>
                    <div className="flex flex-row items-center gap-2">
                        <h1 className="text-3xl font-bold">Multi Project Dashboard</h1>
                        <div className='flex flex-col items-center'>
                            <button
                                className="text-3xl font-bold text-white p-1 my-auto rounded-full hover:bg-accent"
                                onClick={() => { loadDashboardData(); }}
                            >
                                <RefreshIcon className="" />
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-row gap-4">
                        {selectedProjects.length < app.projects.length && (
                            <button
                                onClick={() => resetFilter()}
                                className="button-default"
                            >
                                Show All Projects
                            </button>
                        )}
                        <button
                            onClick={showDashboardOptions}
                            className="button-default"
                        >
                            Select Projects
                        </button>
                        <button
                            onClick={showViewOptions}
                            className="button-default"
                        >
                            View
                        </button>
                    </div>
                </div>
                <div className="flex flex-col h-min justify-normal gap-4 py-2 my-auto">
                    <div className="flex flex-row justify-normal gap-4 px-4 my-auto">
                        {(selectedViewElements & 1) === 1 && <RadialChartComponent actualPercentage={projectProgressActual} plannedPercentage={projectProgressPlanned} isMultiProject={true} onClick={radialClick} />}
                        {(selectedViewElements & 2) === 2 && projectOverview && <OverviewComponent projectOverviewRows={projectOverview} isMultiProject={true} />}
                        {(selectedViewElements & 4) === 4 && projectHealth && <ProjectHealth projectHealth={projectHealth} currentWeek={currentWeek} totalWeeks={totalWeeks} notifications={notifications} setNotifications={setNotifications} isMultiProject={true} handleHealthClick={filterProjectsByHealth} />}
                    </div>

                    <div className="flex flex-row justify-normal gap-4 px-4 my-auto">
                        {(selectedViewElements & 8) === 8 && <WorkstreamProgressChart workstreamProgress={workstreamProgress} isMultiProject={true} />}
                        {(selectedViewElements & 16) === 16 && <WorkstreamHealthReport workstreamHealth={workstreamHealth} isMultiProject={true} showProjectName={(selectedViewElements & 8) !== 8} />}
                    </div>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="flex flex-col max-w-fit">
                        <DialogHeader>
                            <DialogTitle>{dialogTitle}</DialogTitle>
                        </DialogHeader>
                        {dialogContent}
                    </DialogContent>
                </Dialog>
        </div>
    );
}

export default MultiProjectDash